import {
  Button,
  ButtonProps,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import { cloneElement, isValidElement, ReactElement } from "react";

interface FilterButtonProps extends ButtonProps {
  isActive?: boolean;
  icon?: IconType | ReactElement;
}

export default function FilterButton({ children, isActive = false, icon, ...rest }: FilterButtonProps) {
  const inactiveTextColor = useColorModeValue("gray.600", "gray.200");
  const activeBg = useColorModeValue("cdsIndigo.300", "cdsIndigo.500");
  const activeHoverBg = useColorModeValue("cdsIndigo.200", "cdsIndigo.400");
  const inactiveHoverBg = useColorModeValue("gray.50", "whiteAlpha.200");

  const renderIcon = () => {
    if (!icon) return undefined;

    // If it's a React element (like <Box>), clone it with color prop
    if (isValidElement(icon)) {
      return cloneElement(icon as ReactElement, {
        ...((icon as ReactElement).props || {}),
        color: isActive ? "white" : (icon as ReactElement).props?.color || inactiveTextColor,
      });
    }

    // If it's an IconType (react-icons component)
    return <Icon as={icon as IconType} boxSize={3.5} color={isActive ? "white" : inactiveTextColor} />;
  };

  return (
    <Button
      size="sm"
      variant="outline"
      colorScheme="gray"
      h="28px"
      minH="28px"
      px={2.5}
      fontSize="13px"
      fontWeight="medium"
      bg={isActive ? activeBg : undefined}
      color={isActive ? "white" : inactiveTextColor}
      _hover={{
        bg: isActive ? activeHoverBg : inactiveHoverBg,
      }}
      leftIcon={renderIcon()}
      {...rest}
    >
      {children}
    </Button>
  );
}
